const DB_NAME = 'voicequote-offline';
const STORE_NAME = 'pending-recordings';
const DB_VERSION = 1;

export interface QueueItem {
  id?: number;
  blob: Blob;
  mimeType: string;
  language: string;
  createdAt: number;
}

interface StoredItem {
  id?: number;
  data: ArrayBuffer;
  mimeType: string;
  language: string;
  createdAt: number;
}

function wrapRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    /* istanbul ignore next -- @preserve: IDB errors cannot be triggered in fake-indexeddb */
    request.onerror = () => reject(request.error ?? new Error('IDB request failed'));
  });
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
    };
    request.onsuccess = () => resolve(request.result);
    /* istanbul ignore next -- @preserve: IDB errors cannot be triggered in fake-indexeddb */
    request.onerror = () => reject(request.error ?? new Error('Failed to open database'));
  });
}

export async function saveToQueue(blob: Blob, mimeType: string, language: string): Promise<number> {
  const data = await blob.arrayBuffer();
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const item: StoredItem = { data, mimeType, language, createdAt: Date.now() };
    return await (wrapRequest(store.add(item)) as Promise<number>);
  } finally {
    db.close();
  }
}

export async function getAllPending(): Promise<QueueItem[]> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const stored = (await wrapRequest(store.getAll())) as StoredItem[];
    return stored.map((s) => ({
      id: s.id,
      blob: new Blob([s.data], { type: s.mimeType }),
      mimeType: s.mimeType,
      language: s.language,
      createdAt: s.createdAt,
    }));
  } finally {
    db.close();
  }
}

export async function removeFromQueue(id: number): Promise<void> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await wrapRequest(store.delete(id));
  } finally {
    db.close();
  }
}

export async function clearQueue(): Promise<void> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await wrapRequest(store.clear());
  } finally {
    db.close();
  }
}

export async function getPendingCount(): Promise<number> {
  const db = await openDB();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    return await wrapRequest(store.count());
  } finally {
    db.close();
  }
}
